import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useApi } from '@/hooks/useApi';
import { toast } from '@/hooks/use-toast';
import { useRBAC } from '@/contexts/rbac-context';
import { approvalClient, APPROVAL_MENU_KEY } from '@/lib/approval-client';
import type { ApprovalBox, ApprovalProgram, ApprovalRequestDetail } from '@/types/approval';
import {
  formatDateTime,
  formatNumber,
  historyLabel,
  notifyApprovalChanged,
  requestDataValue,
  resultLabel,
  statusLabel,
  StatusBadge,
  useApprovalNav,
  ApprovalStampRow,
  ApprovalSection,
  PriceDelta,
  ContractPriceChange,
  contractPriceLabel,
} from './approval-common';
import { ArrowLeft, ChevronRight, RefreshCw } from 'lucide-react';

type ActionKey = 'approve' | 'reject' | 'cancel' | 'cancel-request' | 'cancel-approve' | 'cancel-reject';

const ACTION_META: Record<
  ActionKey,
  { label: string; variant: 'default' | 'destructive' | 'outline'; needsComment: boolean; note: string }
> = {
  approve: { label: '승인', variant: 'default', needsComment: false, note: '승인 후에는 기안자만 취소를 요청할 수 있습니다.' },
  reject: { label: '반려', variant: 'destructive', needsComment: true, note: '반려 사유를 입력해야 처리됩니다.' },
  cancel: { label: '자진취소', variant: 'outline', needsComment: false, note: '결재를 취소하고 종결합니다. 대기 중인 결재자에게 결재불요가 통보됩니다.' },
  'cancel-request': { label: '취소요청', variant: 'outline', needsComment: true, note: '취소 사유를 입력해야 처리됩니다.' },
  'cancel-approve': { label: '취소승인', variant: 'default', needsComment: false, note: '요청이 취소 처리되고 적용 예정 단가가 취소됩니다.' },
  'cancel-reject': { label: '취소반려', variant: 'destructive', needsComment: true, note: '반려 사유를 입력해야 처리됩니다. 요청은 원래 상태로 돌아갑니다.' },
};

/** 파괴적 액션은 기본 액션과 떼어 놓는다 - 같은 자리에서 반복 클릭하다 잘못 누른다 */
const DESTRUCTIVE: ActionKey[] = ['reject', 'cancel', 'cancel-request', 'cancel-reject'];

/**
 * 결재 상세. 라우트(`/approvals/{id}`, 알림 딥링크)로도 쓰고 목록 화면의 오른쪽 칸으로도 쓴다.
 * 목록에서 쓸 때는 함·프로그램을 props 로 받으므로 메뉴 키 역추적이 첫 시도에 끝난다.
 */
export function ApprovalDetailScreen({
  id: idProp,
  box,
  program: programProp,
  onDone,
  showBack,
}: {
  id?: number;
  /** 목록 안에 들어간 경우. 객체로 받으면 부모가 렌더될 때마다 문서를 다시 읽는다 */
  box?: ApprovalBox;
  program?: ApprovalProgram;
  /** 처리 후 할 일 - 목록은 다시 읽고(reload), 라우트는 들어온 함으로 돌아간다 */
  onDone?: () => void;
  showBack?: boolean;
}) {
  const { id } = useParams();
  const requestId = idProp ?? Number(id);
  const nav = useApprovalNav();
  const { canAccessMenu } = useRBAC();

  const candidates = useMemo(() => {
    const accessible = Object.entries(APPROVAL_MENU_KEY)
      .filter(([key]) => canAccessMenu(key))
      .map(([, v]) => ({ box: v.box, program: v.program }));
    // 아는 함을 먼저 시도한다 - 순서가 고정이면 결재함 건마다 403/404를 두세 번 맞는다
    return box && programProp
      ? [{ box, program: programProp }, ...accessible.filter(c => c.box !== box || c.program !== programProp)]
      : accessible;
  }, [canAccessMenu, box, programProp]);

  const { data, loading, error, refetch } = useApi(
    () => approvalClient.findDetail(requestId, candidates),
    [requestId, candidates]
  );

  const foundBox = box ?? data?.box;
  const foundProgram = programProp ?? data?.program;
  const goBack = useCallback(
    () => (foundBox && foundProgram ? nav.goList(foundBox, foundProgram) : nav.goHome()),
    [foundBox, foundProgram, nav]
  );

  if (loading && !data) {
    return (
      <section className='space-y-3 rounded-lg border bg-card p-5'>
        <Skeleton className='h-8 w-64' />
        <Skeleton className='h-24 w-full' />
        <Skeleton className='h-40 w-full' />
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className='flex min-h-[16rem] items-center justify-center rounded-lg border bg-card'>
        <div className='space-y-3 text-center'>
          <p className='text-sm text-muted-foreground'>
            요청을 불러오지 못했습니다. 접근 권한이 없거나 삭제된 건일 수 있습니다.
          </p>
          <Button variant='outline' size='sm' onClick={() => refetch()}>
            다시 시도
          </Button>
        </div>
      </section>
    );
  }

  return (
    <ApprovalDocument
      key={requestId}
      detail={data.detail}
      box={foundBox ?? data.box}
      program={data.program}
      onRefresh={refetch}
      onBack={showBack ? goBack : undefined}
      onDone={onDone ?? goBack}
    />
  );
}

/**
 * 결재 문서. 목록 화면에서는 오른쪽 칸에, 딥링크 라우트에서는 전체 폭으로 같은 것을 쓴다.
 * 처리 후 동작만 다르다 - 분할 화면은 목록을 새로 읽고(onProcessed), 라우트는 들어온 함으로 돌아간다(onBack).
 */
function ApprovalDocument({
  detail: current,
  box,
  program,
  onRefresh,
  onBack,
  onDone,
}: {
  detail: ApprovalRequestDetail;
  /** 기안함은 결재선이 읽기전용이다 (B210 §3, B220 §3.4) - 결재 액션과 자물쇠 안내는 결재함에만 */
  box: ApprovalBox;
  program: ApprovalProgram;
  onRefresh: () => void;
  /** 있으면 '목록' 버튼을 단다 (라우트로 들어온 경우) */
  onBack?: () => void;
  onDone: () => void;
}) {
  const [pending, setPending] = useState<ActionKey | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  /** 결재선 탭에서 고른 단계. null 이면 접힌 상태 */
  const [selectedStep, setSelectedStep] = useState<number | null>(null);

  const requestId = current.request.id;

  const run = useCallback(
    async (action: ActionKey, text?: string) => {
      setBusy(true);
      try {
        const updated =
          action === 'cancel' || action === 'cancel-request'
            ? await approvalClient.drafterAction(program, requestId, action, text)
            : await approvalClient.approverAction(program, requestId, action, text);
        toast({ title: `${ACTION_META[action].label} 처리 완료`, description: updated.request.documentNo });
        setPending(null);
        setComment('');
        notifyApprovalChanged(); // 레일의 내 차례 건수도 같이 줄어야 한다
        // 결과는 토스트가 알린다. 처리한 문서에 머물면 다음 건까지 수동 이동이다
        onDone();
      } catch (err) {
        toast({
          title: `${ACTION_META[action].label} 실패`,
          description: err instanceof Error ? err.message : '처리하지 못했습니다',
          variant: 'destructive',
        });
      } finally {
        setBusy(false);
      }
    },
    [onDone, program, requestId]
  );

  // 네이티브 confirm 은 문서 정보가 없고 Enter 가 기본값이라 반복 처리에서 통과 의식이 된다
  const start = (action: ActionKey) => {
    setComment('');
    setPending(action);
  };

  const r = current.request;
  // 기안자 액션은 단계와 무관해 카드 바닥에 둔다. 결재 액션은 결재선 탭의 활성 단계 안으로 들어간다 (A340 §2.3)
  const drafterActions: ActionKey[] = [
    ...(current.actions.canCancel ? (['cancel'] as ActionKey[]) : []),
    ...(current.actions.canCancelRequest ? (['cancel-request'] as ActionKey[]) : []),
  ];

  const steps = current.lines.filter(line => line.stepOrder > 0);
  // 취소 불가 사유는 문서의 사실이라 기안자가 아니어도 보여 준다 (B210 PRD 4.5-B 문구)
  const cancelBlockedNote = current.isApplied
    ? '이미 단가가 적용 완료되어 취소 처리가 불가능합니다.'
    : current.hasSalesData
      ? '해당 적용단가로 이미 판매자료가 발생되어 있어 취소가 불가능합니다.'
      : null;
  // 취소요청 건의 처리 차례는 가상 취소결재 행(stepOrder 0)이다 (I-4)
  const inbox = box === 'inbox';
  const activeStep = !inbox
    ? null
    : r.status === 'CANCEL_REQUESTED'
      ? 0
      : r.status === 'SUBMITTED' || r.status === 'IN_PROGRESS'
        ? r.currentStepOrder
        : null;
  // 고르기 전 기본값: 처리할 차례인 단계 > 의견이 남은 마지막 단계.
  // 결재자는 바로 처리할 수 있어야 하고, 종결 문서는 반려 사유를 찾으려 도장을 눌러 볼 일이 없어야 한다
  const selected =
    current.lines.find(line => line.stepOrder === selectedStep) ??
    current.lines.find(line => activeStep !== null && line.stepOrder === activeStep) ??
    [...current.lines].reverse().find(line => line.comment) ??
    null;
  const onActiveStep = selected !== null && activeStep !== null && selected.stepOrder === activeStep;
  const stepActions: ActionKey[] =
    selected?.stepOrder === 0
      ? current.actions.canCancelApprove
        ? (['cancel-approve', 'cancel-reject'] as ActionKey[])
        : []
      : current.actions.canApprove
        ? (['approve', 'reject'] as ActionKey[])
        : [];

  return (
    <section className='flex min-h-0 flex-col overflow-hidden rounded-lg border bg-card'>
      <div className='flex flex-wrap items-center gap-2 border-b px-5 py-3'>
        {onBack && (
          <Button variant='ghost' size='sm' className='-ml-2' onClick={onBack}>
            <ArrowLeft className='mr-1 size-4' />
            목록
          </Button>
        )}
        <span className='font-mono text-xs text-muted-foreground'>{r.documentNo}</span>
        <StatusBadge status={r.status} />
        <h2 className='min-w-0 break-keep text-sm font-semibold'>
          {r.orgName ?? r.orgCd ?? '-'} · {itemSummary(program, r.requestData)}
        </h2>
        <span className='ml-auto text-xs text-muted-foreground'>요청 {formatDateTime(r.createdAt)}</span>
        <Button variant='ghost' size='sm' title='새로고침' disabled={busy} onClick={onRefresh}>
          <RefreshCw className='size-4' />
        </Button>
      </div>

      {/* PRD 하단 2분할 - 좌 단가 변경 상세 / 우 결재 처리 */}
      <div className='grid min-h-0 flex-1 grid-cols-1 xl:grid-cols-2 xl:divide-x'>
        <div className='min-h-0 space-y-4 overflow-y-auto p-5'>
          <h3 className='text-sm font-semibold'>단가 변경 상세</h3>

          {/* 이 문서의 결론은 '얼마에서 얼마로' 다. 표 안에 묻지 않고 맨 위에 크게 둔다 */}
          <div className='flex flex-wrap items-end gap-x-8 gap-y-3 rounded-lg border bg-background px-4 py-3'>
            <div>
              <p className='text-xs text-muted-foreground'>{program === 'store-price' ? '현재 단가' : '현재 계약'}</p>
              <p className='text-xl tabular-nums text-muted-foreground line-through'>
                {program === 'store-price'
                  ? formatNumber(r.requestData?.currentPrice)
                  : contractPriceLabel(r.requestData?.currentPrice, r.requestData?.currentPriceAppMethod, r.requestData?.currentPriceAppMethodName)}
              </p>
            </div>
            <ChevronRight className='mb-1 size-5 text-muted-foreground' />
            <div>
              <p className='text-xs text-muted-foreground'>{program === 'store-price' ? '요청 단가' : '요청 계약'}</p>
              <p className='text-3xl font-semibold leading-none tabular-nums'>
                {program === 'store-price'
                  ? formatNumber(r.requestData?.requestedPrice)
                  : contractPriceLabel(r.requestData?.requestedPrice, r.requestData?.priceAppMethod, r.requestData?.priceAppMethodName)}
              </p>
            </div>
            {/* 거래처 계약단가는 단가적용방법마다 단위가 달라 증감을 보여주지 않는다 */}
            {program === 'store-price' && (
              <div className='mb-1 text-sm'>
                <PriceDelta current={r.requestData?.currentPrice} requested={r.requestData?.requestedPrice} deltaOnly />
              </div>
            )}
            <div className='mb-1 ml-auto text-right'>
              <p className='text-xs text-muted-foreground'>적용일</p>
              <p className='font-medium tabular-nums'>{requestDataValue(r.requestData, 'applyDate')}</p>
            </div>
          </div>

          <div className='grid grid-cols-[6rem_1fr] border-l border-t text-sm sm:grid-cols-[6.5rem_1fr_6.5rem_1fr]'>
            <Row label='요청부서' value={r.orgName ?? r.orgCd ?? '-'} />
            <Row label='요청자' value={r.drafterName ?? '-'} />
            {program === 'store-price' ? (
              <>
                <Row label='상품명' value={requestDataValue(r.requestData, 'productName')} />
                <Row label='적용구분' value={requestDataValue(r.requestData, 'applyTypeName')} />
                <Row label='적용차수' value={requestDataValue(r.requestData, 'applyRound')} />
                <Row label='변동액' value={formatNumber(r.requestData?.differenceAmount)} />
              </>
            ) : (
              <>
                <Row label='거래처' value={requestDataValue(r.requestData, 'dealerName')} />
                <Row label='상품명' value={requestDataValue(r.requestData, 'productName')} />
                <Row label='판매구분' value={requestDataValue(r.requestData, 'oilSaleTypeName')} />
                <Row label='적용구분' value={requestDataValue(r.requestData, 'applyTypeName')} />
                <Row label='단가적용방법' value={requestDataValue(r.requestData, 'priceAppMethodName')} />
              </>
            )}
            {/* 사유 라벨은 취소 상태에서 취소요청사유로 바뀐다 (PRD 공통) */}
            {current.cancelReason ? (
              <Row label='취소요청사유' value={current.cancelReason} wide />
            ) : (
              <Row label='승인요청사유' value={requestDataValue(r.requestData, 'requestReason')} wide />
            )}
          </div>
        </div>

        <div className='flex min-h-0 flex-col'>
          <div className='min-h-0 flex-1 space-y-4 overflow-y-auto p-5'>
            <h3 className='text-sm font-semibold'>결재 처리</h3>

            <div className='grid grid-cols-[6rem_1fr] border-l border-t text-sm sm:grid-cols-[6.5rem_1fr_6.5rem_1fr]'>
              <Row label='처리상태' value={statusLabel(r.status)} />
              <Row
                label='현재단계'
                value={r.currentStepOrder ? `${r.currentStepOrder} / ${steps.length}` : `- / ${steps.length}`}
              />
              <Row label='반영방식' value={r.applyMethod === 'EVENT_TRIGGERED' ? 'POS 마감 시 적용' : '승인 즉시 반영'} />
              <Row label='승인일시' value={formatDateTime(r.approvedAt)} />
            </div>

            {/* 결재선 탭 - 도장을 누르면 그 단계의 의견·결재일시를 아래에 편다 (PRD 결재선 탭 + 선택 탭 상세) */}
            <div className='space-y-2'>
              <ApprovalStampRow
                request={r}
                lines={current.lines}
                selectedStep={selected?.stepOrder ?? null}
                onSelect={setSelectedStep}
              />
              {selected && (
                <div className='rounded-md border bg-muted/30 p-3 text-sm'>
                  <div className='flex flex-wrap items-center gap-2 text-xs text-muted-foreground'>
                    <span>{selected.stepLabel ?? (selected.stepOrder === 0 ? '취소결재' : `${selected.stepOrder}차`)}</span>
                    <span>·</span>
                    <span>{selected.approverName ?? `#${selected.approverId}`}</span>
                    {selected.isDelegated && <span>(대리)</span>}
                    <span>·</span>
                    <span>{resultLabel(selected.result)}</span>
                    <span>·</span>
                    <span>{formatDateTime(selected.processedAt)}</span>
                  </div>
                  {selected.comment && <p className='mt-1 whitespace-pre-wrap'>{selected.comment}</p>}
                  {!selected.comment && !onActiveStep && (
                    <p className='mt-1 text-muted-foreground'>남긴 의견이 없습니다.</p>
                  )}

                  {onActiveStep && stepActions.length > 0 && (
                    <div className='mt-3 space-y-2 border-t pt-3'>
                      <Textarea
                        rows={3}
                        aria-label='결재 의견'
                        placeholder='의견을 입력하세요 (반려는 필수)'
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                        maxLength={500}
                      />
                      <div className='flex justify-end gap-2'>
                        {stepActions.map(action => (
                          <Button
                            key={action}
                            size='sm'
                            variant={ACTION_META[action].variant}
                            disabled={busy || (ACTION_META[action].needsComment && comment.trim() === '')}
                            title={ACTION_META[action].note}
                            onClick={() => run(action, comment.trim() || undefined)}
                          >
                            {ACTION_META[action].label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {onActiveStep && stepActions.length === 0 && (
                    <p className='mt-3 border-t pt-3 text-xs text-muted-foreground'>
                      🔒 현재 로그인은 이 결재단계에 대한 처리 권한이 없습니다
                    </p>
                  )}
                </div>
              )}
            </div>

            {cancelBlockedNote && (
              <p className='rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900'>
                {cancelBlockedNote}
              </p>
            )}

            <ApprovalSection title='처리 이력'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className='w-32'>액션</TableHead>
                    <TableHead className='w-20'>단계</TableHead>
                    <TableHead>처리자</TableHead>
                    <TableHead>상세</TableHead>
                    <TableHead className='w-44'>일시</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {current.histories.map(h => (
                    <TableRow key={h.id}>
                      <TableCell>{historyLabel(h.action)}</TableCell>
                      <TableCell>{h.stepOrder ?? '-'}</TableCell>
                      <TableCell>{h.actorName ?? (h.actorId ? `#${h.actorId}` : '시스템')}</TableCell>
                      <TableCell className='text-xs text-muted-foreground'>{historyDetail(h.detail)}</TableCell>
                      <TableCell className='tabular-nums'>{formatDateTime(h.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ApprovalSection>
          </div>

          {drafterActions.length > 0 && (
            // 문서 칸 바닥에 고정한다 (뷰포트가 아니라) - 분할 화면에서 목록 위로 떠다니면 안 된다
            <div className='flex flex-wrap items-center justify-end gap-2 border-t bg-card px-5 py-3'>
              {drafterActions.map(action => (
                <Button key={action} size='sm' variant='outline' disabled={busy} onClick={() => start(action)}>
                  {ACTION_META[action].label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={pending !== null} onOpenChange={open => !open && setPending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{pending ? ACTION_META[pending].label : ''}</DialogTitle>
            <DialogDescription>{pending ? ACTION_META[pending].note : ''}</DialogDescription>
          </DialogHeader>

          {/* 어느 건을 처리하는지 확인창 안에서 보여준다 - 반복 처리에서 문서를 착각한다 */}
          <div className='space-y-1 rounded-md border bg-muted/40 p-3 text-sm'>
            <div className='font-mono text-xs text-muted-foreground'>{r.documentNo}</div>
            <div>{r.orgName ?? r.orgCd ?? '-'}</div>
            <div>{itemSummary(program, r.requestData)}</div>
            {program === 'store-price' ? (
              <PriceDelta current={r.requestData?.currentPrice} requested={r.requestData?.requestedPrice} />
            ) : (
              <ContractPriceChange data={r.requestData} />
            )}
          </div>

          {pending && ACTION_META[pending].needsComment && (
            <Textarea
              rows={4}
              aria-label='사유'
              placeholder='사유를 입력하세요'
              value={comment}
              onChange={e => setComment(e.target.value)}
              maxLength={500}
            />
          )}

          <DialogFooter>
            <Button variant='outline' onClick={() => setPending(null)} disabled={busy}>
              닫기
            </Button>
            <Button
              variant={pending && DESTRUCTIVE.includes(pending) ? 'destructive' : 'default'}
              disabled={busy || (pending !== null && ACTION_META[pending].needsComment && comment.trim() === '')}
              onClick={() => pending && run(pending, comment.trim() || undefined)}
            >
              {pending ? ACTION_META[pending].label : '확인'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

/** 제목·확인창에 같이 쓰는 한 줄 요약 (유종/적용구분 또는 거래처/유종) */
function itemSummary(program: ApprovalProgram, data: Record<string, unknown> | null): string {
  return program === 'store-price'
    ? `${requestDataValue(data, 'productName')} / ${requestDataValue(data, 'applyTypeName')}`
    : `${requestDataValue(data, 'dealerName')} / ${requestDataValue(data, 'productCode')}`;
}

/** 품의서 형태의 라벨/값 한 칸. wide 는 값 칸이 남은 열을 모두 차지한다 */
function Row({ label, value, wide }: { label: string; value: string; wide?: boolean }) {
  return (
    <>
      {/* 넓은 행은 항상 줄 첫 칸에서 시작해야 한다 - 앞 행 개수가 홀수면 라벨/값이 어긋난다 */}
      <div
        className={`flex items-center border-b border-r bg-muted/40 px-3 py-2 text-xs text-muted-foreground ${wide ? 'sm:col-start-1' : ''}`}
      >
        {label}
      </div>
      <div
        className={`border-b border-r px-3 py-2 font-medium break-words tabular-nums ${wide ? 'sm:col-span-3' : ''}`}
      >
        {value}
      </div>
    </>
  );
}

/** 처리 이력 detail(JSONB) 중 사용자에게 의미 있는 키만 - target_key 같은 내부 키는 감춘다 */
const HISTORY_DETAIL_LABEL: Record<string, string> = {
  reason: '사유',
  comment: '의견',
  from_status: '이전상태',
};

function historyDetail(detail: Record<string, unknown> | null): string {
  const parts = Object.entries(detail ?? {})
    .filter(([key, value]) => HISTORY_DETAIL_LABEL[key] && value !== null && value !== '')
    .map(([key, value]) => {
      const text = key === 'from_status' ? statusLabel(String(value)) : String(value);
      return `${HISTORY_DETAIL_LABEL[key]}: ${key === 'reason' && text === 'SELF' ? '기안자 본인 단계' : text}`;
    });
  return parts.length > 0 ? parts.join(', ') : '-';
}
